// Pro-level animated solar dashboard. Mobile-first — fits inside a phone
// viewport without scrolling.
//
// Field map (firmware spec):
//   d18 → solar.pvPower_kW       (Solar generation, ≥ 0)
//   d13 → meter.totalPower_kW    (EB / grid power, SIGNED: + import, − export)
//   d20 → solar.gridVoltage      (used to flag Off-grid)
//   d16 → solar.pvToday_kWh
//   d17 → solar.totalEnergy_kWh
//   d14 → meter.import_kWh_total
//   d15 → meter.export_kWh_total
//   isonline → ON / OFF

import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SolarPowerOutlinedIcon from "@mui/icons-material/SolarPowerOutlined";
import ElectricMeterOutlinedIcon from "@mui/icons-material/ElectricMeterOutlined";
import BatteryFullOutlinedIcon from "@mui/icons-material/BatteryFullOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import WifiOffOutlinedIcon from "@mui/icons-material/WifiOffOutlined";
import { useDispatch, useSelector } from "react-redux";
import { fetchMyTelemetry } from "../features/telemetry/telemetrySlice";
import Gauge from "../features/telemetry/Gauge";
import FlowOverlay from "../features/telemetry/FlowOverlay";
import { useAnimatedNumber } from "../features/telemetry/AnimatedNumber";

// ── helpers ────────────────────────────────────────────────────────
const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "string" ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : null;
};
const fmtClock = (d) => {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  const time = d.toLocaleTimeString("en-GB", { hour12: false });
  return `${dd}/${mm}/${yy} ${time}`;
};
const dayShort = (d) =>
  ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()];
const ago = (iso) => {
  if (!iso) return "no data yet";
  const sec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return `${Math.round(sec)}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
};

// Money: Indian-style grouping (lakh / crore)
const RUPEE_PER_KWH = 3.5;
const inrFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });
const inr = (n) => `₹ ${inrFmt.format(n || 0)}`;
const kwhFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });
const kwh = (n) => `${kwhFmt.format(n || 0)}`;

// ── pulsing ON/OFF bubble ───────────────────────────────────────────
const StateBubble = ({ on, size = 88 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: "50%",
      border: `${size > 70 ? 4 : 3}px solid ${on ? "#22c55e" : "#c2c8d2"}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "background.paper",
      boxShadow: on
        ? "0 6px 24px rgba(34,197,94,0.25), 0 2px 6px rgba(13,46,117,0.08)"
        : "0 4px 16px rgba(0,0,0,0.08)",
      animation: on ? "pulse-ring 2.2s ease-out infinite" : "pulse-ring-off 3s ease-out infinite",
      flexShrink: 0,
      transition: "border-color 0.3s ease",
    }}
  >
    <Typography
      sx={{
        fontWeight: 800,
        color: on ? "#22c55e" : "#9aa1ad",
        letterSpacing: 1.2,
        fontSize: size > 70 ? "1rem" : "0.75rem",
      }}
    >
      {on ? "ON" : "OFF"}
    </Typography>
  </Box>
);

// ── compact stat card ──────────────────────────────────────────────
const StatCard = ({ icon, label, value, unit, accent, pill, rupees, delay = "" }) => {
  const animValue = useAnimatedNumber(Number(value) || 0);
  const animInr = useAnimatedNumber(Number(rupees) || 0);
  return (
    <Box
      className={`fade-up${delay ? ` fade-up-delay-${delay}` : ""}`}
      sx={{
        flex: 1,
        minWidth: 0,
        p: { xs: 1, sm: 1.75 },
        borderRadius: { xs: 2, sm: 3 },
        backgroundColor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 1px 2px rgba(13,46,117,0.04), 0 6px 18px rgba(13,46,117,0.05)",
        position: "relative",
        overflow: "hidden",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 1px 2px rgba(13,46,117,0.04), 0 14px 30px rgba(13,46,117,0.10)",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          right: -28,
          top: -28,
          width: 76,
          height: 76,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${accent}28 0%, ${accent}00 70%)`,
          pointerEvents: "none",
        }}
      />
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} spacing={{ xs: 0.5, sm: 1.25 }}>
        <Box
          sx={{
            width: { xs: 28, sm: 36 },
            height: { xs: 28, sm: 36 },
            borderRadius: 1.5,
            background: accent,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& svg": { fontSize: { xs: 16, sm: 20 } },
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              fontSize: { xs: "0.5625rem", sm: "0.6875rem" },
              lineHeight: 1.2,
              display: "block",
            }}
          >
            {label}
          </Typography>
          <Stack direction="row" alignItems="baseline" spacing={0.5}>
            <Typography
              sx={{
                fontSize: { xs: "1rem", sm: "1.375rem" },
                fontWeight: 800,
                fontFamily: "monospace",
                letterSpacing: -0.5,
                lineHeight: 1.1,
              }}
            >
              {animValue.toFixed(value >= 100 ? 0 : 2)}
            </Typography>
            {unit && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 600, fontSize: { xs: "0.625rem", sm: "0.75rem" } }}
              >
                {unit}
              </Typography>
            )}
          </Stack>
          {rupees != null && (
            <Typography
              sx={{
                fontFamily: "monospace",
                fontWeight: 700,
                fontSize: { xs: "0.75rem", sm: "0.9375rem" },
                color: "text.primary",
                lineHeight: 1.2,
                mt: 0.25,
              }}
            >
              {inr(animInr)}
            </Typography>
          )}
        </Box>
        {pill && (
          <Chip
            size="small"
            label={pill}
            sx={{
              fontWeight: 700,
              fontSize: "0.5625rem",
              height: 18,
              backgroundColor: `${accent}1f`,
              color: accent,
              border: `1px solid ${accent}33`,
              display: { xs: "none", sm: "flex" },
            }}
          />
        )}
      </Stack>
    </Box>
  );
};

// ── live status pill ───────────────────────────────────────────────
const StatusPill = ({ online, offGrid, lastSeen }) => (
  <Stack
    className="fade-up"
    direction="row"
    spacing={0.75}
    alignItems="center"
    sx={{
      px: 1.25,
      py: 0.5,
      borderRadius: 999,
      backgroundColor: "background.paper",
      border: "1px solid",
      borderColor: "divider",
      width: "fit-content",
    }}
  >
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: online ? "#22c55e" : "#9aa1ad",
        animation: online ? "status-dot-blink 1.5s ease-in-out infinite" : "none",
      }}
    />
    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.6875rem", color: online ? "success.main" : "text.secondary" }}>
      {online ? "Live" : "Offline"}
    </Typography>
    <Typography variant="caption" color="text.disabled">·</Typography>
    <Typography variant="caption" sx={{ fontWeight: 600, color: offGrid ? "error.main" : "primary.main", fontSize: "0.6875rem" }}>
      {offGrid ? "Off grid" : "On grid"}
    </Typography>
    <Typography variant="caption" color="text.disabled">·</Typography>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6875rem" }}>
      {lastSeen}
    </Typography>
  </Stack>
);

// ── page ───────────────────────────────────────────────────────────
export default function DashboardPage() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { items } = useSelector((s) => s.telemetry);
  const installedKw = useSelector((s) => s.settings.installedKw);

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Offline-warning popup state. wasOfflineRef tracks the previous offline
  // status so the dialog only auto-opens on the transition into offline,
  // not on every poll (which would re-open it after the user dismisses it).
  const [offlineDialogOpen, setOfflineDialogOpen] = useState(false);
  const wasOfflineRef = useRef(false);

  // Poll the API every 20 s — pure read-only loop, no realtime sockets.
  useEffect(() => {
    dispatch(fetchMyTelemetry());
    const id = setInterval(() => dispatch(fetchMyTelemetry()), 20_000);
    return () => clearInterval(id);
  }, [dispatch]);

  // Measure the energy-flow board so we can scale the gauges to fill it.
  const boardRef = useRef(null);
  const [boardSize, setBoardSize] = useState({ w: 360, h: 280 });
  useEffect(() => {
    if (!boardRef.current || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => {
      setBoardSize({ w: entry.contentRect.width, h: entry.contentRect.height });
    });
    ro.observe(boardRef.current);
    return () => ro.disconnect();
  }, []);

  const row = items[0];
  const t = row?.telemetry;

  // ── Offline > 1 h detection ────────────────────────────────────────
  // A device that hasn't reported for over an hour is treated as fully
  // offline: every reading is forced to 0 (so the customer never sees
  // stale figures) and a popup tells them to check the Wi-Fi / EB supply.
  // We use receivedAt (last time any payload landed) re-checked against
  // the live `now` clock so it flips on its own without new data.
  const HOUR_MS = 60 * 60 * 1000;
  const lastSeenMs = t?.receivedAt ? new Date(t.receivedAt).getTime() : null;
  const offlineOverHour =
    !!t && (lastSeenMs == null || now.getTime() - lastSeenMs > HOUR_MS);

  // Auto-open the popup once when the device crosses into the offline
  // state; auto-close it as soon as fresh data brings it back online.
  useEffect(() => {
    if (offlineOverHour && !wasOfflineRef.current) setOfflineDialogOpen(true);
    if (!offlineOverHour) setOfflineDialogOpen(false);
    wasOfflineRef.current = offlineOverHour;
  }, [offlineOverHour]);

  // Raw signed values from the firmware — all forced to 0 while offline > 1 h.
  const solarKw    = offlineOverHour ? 0 : num(t?.d18);  // solar.pvPower_kW (≥ 0)
  const gridKwRaw  = offlineOverHour ? 0 : num(t?.d13);  // EB power (signed): + import, − export
  const gridVolts  = offlineOverHour ? 0 : num(t?.d20);
  const todayKwh   = offlineOverHour ? 0 : num(t?.d16);
  const totalKwh   = offlineOverHour ? 0 : num(t?.d17);  // solar.totalEnergy_kWh
  const importKwh  = offlineOverHour ? 0 : num(t?.d14);  // meter.import_kWh_total
  const exportKwh  = offlineOverHour ? 0 : num(t?.d15);  // meter.export_kWh_total
  const online     = !offlineOverHour && t?.isonline === 1;
  const offGrid    = !t || gridVolts == null || gridVolts < 50;

  // Home load = Solar generation + signed grid power.
  //   Day, surplus:  solar=5, grid=−2  →  home = 3
  //   Day, deficit:  solar=3, grid=+2  →  home = 5
  //   Night:         solar=0, grid=+x  →  home = x
  // (When offGrid, grid is 0 by definition → home = solar.)
  const gridSigned = offGrid ? 0 : (gridKwRaw || 0);
  const homeKw     = (solarKw || 0) + gridSigned;
  // For the grid gauge we show magnitude; sign goes in the top label.
  const gridKw     = Math.abs(gridSigned);

  // Grid status label — "Importing" / "Exporting" / "On grid" / "Off grid"
  let gridLabel    = "On grid";
  let gridLabelCol = "success.main";
  if (offGrid) {
    gridLabel = "Off grid";  gridLabelCol = "error.main";
  } else if (gridSigned > 0.05) {
    gridLabel = "Importing"; gridLabelCol = "warning.main";
  } else if (gridSigned < -0.05) {
    gridLabel = "Exporting"; gridLabelCol = "success.main";
  }

  // Gauge size scales to the smaller of available width or available height
  // so the whole dashboard fits the viewport without scrolling.
  // Caps: desktop 180 px, tablet/mobile 150 px.
  const horizontalCap = Math.max(100, Math.floor((boardSize.w - 100) / 2));
  // Each row needs gauge + label (~22 px) + a small gap → reserve 56 px
  const verticalCap = Math.max(100, Math.floor((boardSize.h - 56) / 2));
  const gaugeSize = isDesktop
    ? Math.min(180, horizontalCap, verticalCap)
    : Math.min(150, horizontalCap, verticalCap);
  const bubbleSize = isDesktop
    ? Math.max(64, Math.floor(gaugeSize * 0.46))
    : Math.max(50, Math.floor(gaugeSize * 0.42));

  return (
    <Box
      sx={{
        background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 60%)",
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Sticky top bar — logo fills the full header height */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          px: { xs: 1.5, md: 3 },
          py: 0,
          backgroundColor: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
          <Box
            component="img"
            src="/logo.png"
            alt="Surya Solars"
            sx={{
              height: { xs: 60, sm: 54, md: 60 },
              display: "block",
              flexShrink: 0,
              m: 0,
              p: 0,
            }}
          />
          <Typography sx={{ fontWeight: 700, fontFamily: "monospace", fontSize: { xs: "0.6875rem", sm: "0.8125rem", md: "0.9375rem" } }}>
            {fmtClock(now)}{" "}
            <Box component="span" sx={{ color: "primary.main", ml: 0.25 }}>
              {dayShort(now)}
            </Box>
          </Typography>
        </Stack>
      </Stack>

      <Box
        sx={{
          p: { xs: 1, sm: 2, md: 3 },
          maxWidth: 880,
          mx: "auto",
          width: "100%",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {/* Status pill */}
        <Box sx={{ mb: { xs: 1, sm: 1.5 } }}>
          <StatusPill online={online} offGrid={offGrid} lastSeen={ago(t?.receivedAt)} />
        </Box>

        {/* Stats hero — Today and lifetime Solar Generated. */}
        <Stack direction="row" spacing={{ xs: 0.75, sm: 1.5 }} sx={{ mb: { xs: 1, sm: 2 } }}>
          <StatCard
            icon={<SolarPowerOutlinedIcon />}
            label="Today"
            value={todayKwh ?? 0}
            unit="kWh"
            rupees={(todayKwh ?? 0) * RUPEE_PER_KWH}
            accent="#22c55e"
            delay="1"
          />
          <StatCard
            icon={<SolarPowerOutlinedIcon />}
            label="Total Solar Generated"
            value={totalKwh ?? 0}
            unit="kWh"
            rupees={(totalKwh ?? 0) * RUPEE_PER_KWH}
            accent="#16a34a"
            delay="2"
          />
        </Stack>

        {/* Energy-flow board — fills remaining vertical space so the page
            stays scroll-free on desktops. Gauge size adapts to the height. */}
        <Box
          ref={boardRef}
          className="fade-up fade-up-delay-3"
          sx={{
            backgroundColor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: { xs: 2, sm: 3 },
            p: { xs: 2, sm: 3, md: 4 },
            position: "relative",
            boxShadow: "0 1px 3px rgba(13,46,117,0.05), 0 12px 32px rgba(13,46,117,0.07)",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: 260,
          }}
        >
          {/* 2×2 CSS grid: gauges in the corners, SVG flow paths overlay,
              ON bubble centred. Rows are auto-height with a small gap so the
              gauges hug the centre instead of stretching apart. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gridTemplateRows: "auto auto",
              rowGap: { xs: 1, sm: 2, md: 3 },
              alignItems: "center",
              justifyItems: "center",
              position: "relative",
            }}
          >
            <Box sx={{ justifySelf: "start" }}>
              <Gauge
                value={solarKw}
                max={installedKw}
                color="#fbbf24"
                colorEnd="#22c55e"
                icon={<SolarPowerOutlinedIcon />}
                size={gaugeSize}
                label="Solar"
                gradientId="g-solar"
                disabled={!t}
                active={online && (solarKw || 0) > 0}
              />
            </Box>

            <Box sx={{ justifySelf: "end" }}>
              <Gauge
                value={gridKw}
                max={installedKw}
                color={offGrid ? "#9aa1ad" : "#0d2e75"}
                colorEnd={offGrid ? "#c2c8d2" : "#1f49a8"}
                icon={<ElectricMeterOutlinedIcon />}
                size={gaugeSize}
                label="Grid"
                gradientId="g-grid"
                topLabel={gridLabel}
                topLabelColor={gridLabelCol}
                disabled={!t || (offGrid && !offlineOverHour)}
                active={online && !offGrid && gridKw > 0}
              />
            </Box>

            <Box sx={{ justifySelf: "start" }}>
              <Gauge
                disabled
                icon={<BatteryFullOutlinedIcon />}
                size={gaugeSize}
                label="Battery"
                gradientId="g-bat"
              />
            </Box>

            <Box sx={{ justifySelf: "end" }}>
              <Gauge
                value={homeKw}
                max={installedKw}
                color="#ee5b29"
                colorEnd="#fbbf24"
                icon={<HomeOutlinedIcon />}
                size={gaugeSize}
                label="Home"
                gradientId="g-home"
                disabled={!t}
                active={online && Math.abs(homeKw || 0) > 0}
              />
            </Box>

            {/* SVG flow overlay — L-shaped dotted paths + arrows.
                Self-measures so the geometry stays correct on every viewport. */}
            <FlowOverlay
              bubbleSize={bubbleSize}
              gaugeSize={gaugeSize}
              online={online}
              solarKw={solarKw}
              gridSigned={gridSigned}
              homeKw={homeKw}
              offGrid={offGrid}
            />

            {/* Centre ON/OFF bubble */}
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 2,
                pointerEvents: "none",
              }}
            >
              <StateBubble on={online} size={bubbleSize} />
            </Box>
          </Box>
        </Box>

        {/* Bottom: lifetime totals in kWh and ₹ */}
        <Stack
          direction="row"
          spacing={{ xs: 1, sm: 1.5 }}
          sx={{ mt: { xs: 1.5, sm: 2 } }}
        >
          <TotalCard
            label="Total EB Export"
            kWh={exportKwh ?? 0}
            rupees={(exportKwh ?? 0) * RUPEE_PER_KWH}
            accent="#dc2626"
            icon={<ElectricMeterOutlinedIcon />}
          />
          <TotalCard
            label="Total EB Import"
            kWh={importKwh ?? 0}
            rupees={(importKwh ?? 0) * RUPEE_PER_KWH}
            accent="#dc2626"
            icon={<BoltOutlinedIcon />}
          />
        </Stack>
      </Box>

      {/* Offline > 1 h popup — readings are all 0; prompt the customer to
          check connectivity and the EB power supply. */}
      <Dialog
        open={offlineDialogOpen}
        onClose={() => setOfflineDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          <WifiOffOutlinedIcon sx={{ color: "error.main" }} />
          Device offline
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ mb: 1.5, color: "text.secondary", fontSize: "0.9rem" }}>
            This device has been offline for more than an hour, so all readings
            are shown as 0. Please:
          </Typography>
          <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
            <Typography component="li" sx={{ fontSize: "0.875rem" }}>
              Check the Wi-Fi availability at the device.
            </Typography>
            <Typography component="li" sx={{ fontSize: "0.875rem" }}>
              Check the power supply — the EB mains may be down.
            </Typography>
          </Stack>
          {t?.receivedAt && (
            <Typography sx={{ mt: 1.5, fontSize: "0.75rem", color: "text.disabled" }}>
              Last data received {ago(t.receivedAt)}.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={() => setOfflineDialogOpen(false)}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Bottom totals card: kWh + ₹ ─────────────────────────────────────
function TotalCard({ label, kWh: kWhValue, rupees, accent, icon }) {
  const animKwh = useAnimatedNumber(kWhValue);
  const animInr = useAnimatedNumber(rupees);
  return (
    <Box
      className="fade-up fade-up-delay-4"
      sx={{
        flex: 1,
        minWidth: 0,
        p: { xs: 1.25, sm: 2 },
        borderRadius: { xs: 2, sm: 3 },
        backgroundColor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 1px 2px rgba(13,46,117,0.04), 0 6px 18px rgba(13,46,117,0.05)",
        position: "relative",
        overflow: "hidden",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 1px 2px rgba(13,46,117,0.04), 0 12px 26px rgba(13,46,117,0.10)",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          right: -28,
          top: -28,
          width: 90,
          height: 90,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${accent}26 0%, ${accent}00 70%)`,
          pointerEvents: "none",
        }}
      />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: 1.5,
            background: accent,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& svg": { fontSize: 16 },
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontSize: { xs: "0.5625rem", sm: "0.6875rem" },
          }}
        >
          {label}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mb: 0.25 }}>
        <Typography
          sx={{
            fontFamily: "monospace",
            fontWeight: 800,
            fontSize: { xs: "1rem", sm: "1.25rem" },
            letterSpacing: -0.4,
            lineHeight: 1.1,
            color: accent,
          }}
        >
          {kwh(animKwh)}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, fontSize: { xs: "0.625rem", sm: "0.75rem" } }}>
          kWh
        </Typography>
      </Stack>
      <Typography
        sx={{
          fontFamily: "monospace",
          fontWeight: 700,
          fontSize: { xs: "0.875rem", sm: "1rem" },
          color: "text.primary",
        }}
      >
        {inr(animInr)}
      </Typography>
    </Box>
  );
}
